import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { VelatirResponse } from './models';
import { 
  VelatirError, 
  VelatirAPIError, 
  VelatirTimeoutError 
} from './exceptions';
import { 
  ClientConfig, 
  LogLevel, 
  CreateWatchRequestPayload 
} from './types';

export class Client {
  private static readonly DEFAULT_BASE_URL = 'https://api.velatir.com/api/v1';
  private static readonly DEFAULT_TIMEOUT = 10000; // 10 seconds

  private readonly apiKey?: string;
  private readonly baseUrl: string;
  private readonly timeout: number;
  private readonly logLevel: LogLevel;
  private readonly maxRetries: number;
  private readonly retryBackoff: number;
  private httpClient: AxiosInstance;

  constructor(config: ClientConfig = {}) {
    this.apiKey = config.apiKey;
    this.baseUrl = config.baseUrl || Client.DEFAULT_BASE_URL;
    this.timeout = config.timeout || Client.DEFAULT_TIMEOUT;
    this.logLevel = this.parseLogLevel(config.logLevel);
    this.maxRetries = config.maxRetries || 3;
    this.retryBackoff = config.retryBackoff || 0.5;

    this.httpClient = this.createHttpClient();
  }

  private parseLogLevel(level?: LogLevel | number): LogLevel {
    if (level === undefined || level === null) {
      return LogLevel.ERROR;
    }
    
    if (typeof level === 'number') {
      return level as LogLevel;
    }
    
    return level;
  }

  private createHttpClient(): AxiosInstance {
    const version = '0.1.0'; // This should match package.json version
    
    return axios.create({
      baseURL: this.baseUrl,
      timeout: this.timeout,
      headers: {
        'X-API-Key': this.apiKey || '',
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'User-Agent': `Velatir-TypeScript/${version}`,
      },
    });
  }

  private log(level: LogLevel, message: string, data?: unknown): void {
    if (this.logLevel >= level) {
      const timestamp = new Date().toISOString();
      const levelName = LogLevel[level];
      const logMessage = data 
        ? `${timestamp} - velatir - ${levelName} - ${message} - ${JSON.stringify(data)}`
        : `${timestamp} - velatir - ${levelName} - ${message}`;

      switch (level) {
        case LogLevel.ERROR:
          console.error(logMessage);
          break;
        case LogLevel.INFO:
          console.info(logMessage);
          break;
        case LogLevel.DEBUG:
          console.debug(logMessage);
          break;
        default:
          console.log(logMessage);
      }
    }
  }

  private async request<T>(
    method: string,
    path: string,
    params?: Record<string, unknown>,
    data?: unknown
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      try {
        this.log(LogLevel.INFO, `Making ${method} request to ${url}`);
        if (data) {
          this.log(LogLevel.DEBUG, 'Request payload', data);
        }
        if (params) {
          this.log(LogLevel.DEBUG, 'Request params', params);
        }

        const startTime = Date.now();
        
        const response: AxiosResponse<T> = await this.httpClient.request({
          method,
          url: path,
          params,
          data,
        });

        const duration = Date.now() - startTime;
        this.log(LogLevel.DEBUG, `Request completed in ${duration}ms`);
        this.log(LogLevel.INFO, `Received response from ${url} - ${response.status}`);
        this.log(LogLevel.DEBUG, 'Response data', response.data);

        return response.data;
      } catch (error) {
        lastError = error as Error;
        
        const shouldRetry = this.shouldRetry(error as AxiosError, attempt);
        
        if (!shouldRetry || attempt >= this.maxRetries) {
          break;
        }

        const backoff = this.retryBackoff * Math.pow(2, attempt) * 1000;
        
        this.log(
          LogLevel.INFO,
          `Request failed, retrying in ${backoff}ms (${attempt + 1}/${this.maxRetries})`,
          (error as AxiosError).message
        );

        await this.sleep(backoff);
      }
    }

    throw this.handleError(lastError as AxiosError);
  }

  private shouldRetry(error: AxiosError, attempt: number): boolean {
    if (attempt >= this.maxRetries) {
      return false;
    }

    // Retry on network errors, timeout errors, or 5xx status codes
    return !!(
      error.code === 'ECONNABORTED' ||
      error.code === 'ENOTFOUND' ||
      error.code === 'ECONNRESET' ||
      error.code === 'ETIMEDOUT' ||
      (error.response && error.response.status >= 500)
    );
  }

  private handleError(error: AxiosError): Error {
    if (error.response) {
      let message = error.message;
      let code: string | number = error.response.status;
      
      try {
        const errorData = error.response.data as { message?: string; code?: string | number };
        if (errorData.message) {
          message = errorData.message;
        }
        if (errorData.code) {
          code = errorData.code;
        }
      } catch {
        // Ignore JSON parsing errors
      }

      this.log(LogLevel.ERROR, `API error: ${message}`, error.response.data);

      return new VelatirAPIError(
        message,
        code,
        error.response.status,
        JSON.stringify(error.response.data)
      );
    } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      this.log(LogLevel.ERROR, 'Request timed out', error.message);
      return new VelatirTimeoutError(`Request timed out after ${this.maxRetries} retries: ${error.message}`);
    } else {
      this.log(LogLevel.ERROR, 'Communication error', error.message);
      return new VelatirError(`Error communicating with Velatir API: ${error.message}`);
    }
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async createWatchRequest(
    functionName: string,
    args: Record<string, unknown>,
    doc?: string,
    metadata?: Record<string, unknown>
  ): Promise<VelatirResponse> {
    const payload: CreateWatchRequestPayload = {
      functionname: functionName,
      args,
      doc,
      metadata: metadata || {},
    };

    const response = await this.request<{ requestId: string; state: 'approved' | 'denied' | 'pending' }>(
      'POST',
      '/watches',
      undefined,
      payload
    );

    return new VelatirResponse(response);
  }

  async getWatchStatus(requestId: string): Promise<VelatirResponse> {
    const response = await this.request<{ requestId: string; state: 'approved' | 'denied' | 'pending' }>(
      'GET',
      `/watches/${requestId}`
    );

    return new VelatirResponse(response);
  }

  async waitForApproval(
    requestId: string,
    pollingInterval: number = 5000,
    maxAttempts?: number
  ): Promise<VelatirResponse> {
    let attempts = 0;

    this.log(LogLevel.INFO, `Waiting for approval of request ${requestId}`);

    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (maxAttempts && attempts >= maxAttempts) {
        const errorMsg = `Max polling attempts (${maxAttempts}) reached waiting for request ${requestId}`;
        this.log(LogLevel.ERROR, errorMsg);
        throw new VelatirTimeoutError(errorMsg);
      }

      const response = await this.getWatchStatus(requestId);

      if (response.state !== 'pending') {
        this.log(
          LogLevel.INFO,
          `Request ${requestId} ${response.state}`,
          { state: response.state }
        );
        return response;
      }

      attempts++;
      this.log(
        LogLevel.DEBUG,
        `Request ${requestId} still pending, attempt ${attempts}${maxAttempts ? `/${maxAttempts}` : ''}`,
        { attempt: attempts, maxAttempts }
      );

      await this.sleep(pollingInterval);
    }
  }
}